import React, { useState, useEffect, useCallback } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { useSearch } from "../searchServices";
import { anyTrue, isBlank, notBlank } from "../../util/ext";
import {
  toUpper,
  equals,
  findIndex,
  includes,
  without,
  append,
  map,
  mergeRight,
  propEq,
  filter,
} from "ramda";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faX } from "@fortawesome/pro-regular-svg-icons";
import clsx from "clsx";

import { useAddToModule } from "../modules/moduleServices";
import { useAddToAnthology } from "../anthologies/anthologyServices";
import { useAddToCollection } from "../collections/collectionServices";
import { useDeleteCollectionParent } from "../collections/collectionServices";

import AddIcon from "@mui/icons-material/Add";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";

import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import VirtualizedListbox from "../../components/VirtualizedListbox";
import AddMediaToParentMenuItem from "./AddMediaToParentMenuItem";
import { useDebounce } from "../../util/hooks";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { useSearchQuery } from "./use-searchQuery";

const LISTBOX_HEIGHT = 275;

const useStyles = makeStyles(theme => ({
  autocomplete: {
    padding: theme.spacing(0, 2),
  },

  disabled: {
    opacity: 0.5,
  },

  inputRoot: {
    color: theme.palette.primary.darker,
    flexGrow: 1,
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: `2px solid ${theme.palette.primary.brandPrimary}`,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: `2px solid ${theme.palette.primary.brandPrimary}`,
    },
  },

  inputFocused: {
    borderColor: theme.palette.primary.brandPrimary,
  },

  footer: {
    padding: theme.spacing(1.5, 2),
    position: "absolute",
    bottom: 0,
    zIndex: 3000,
    background: "white",
  },

  inputInput: {
    width: "100%",
    padding: theme.spacing(2.5),
  },

  listbox: {
    height: LISTBOX_HEIGHT,
    overflowY: "auto",
    marginTop: theme.spacing(2.5),
    maxHeight: 280,
    "@media (min-height: 600px)": {
      maxHeight: 390,
    },
  },

  menuItem: {
    height: 55,
    "&:hover": {
      backgroundColor: "transparent",
      color: theme.palette.primary.brandPrimary,
    },
  },

  moreButtonContainer: {
    "&:hover": {
      backgroundColor: "transparent",
    },
  },

  moreButton: {
    width: "100%",
  },

  outline: {
    borderColor: "transparent",
  },

  paper: {
    boxShadow: "none",
  },

  search: {
    backgroundColor: theme.palette.primary.light,
    borderRadius: "4px",
    position: "relative",
    width: "95%",
    display: "flex",
    alignItems: "center",
    margin: "auto",
  },

  searchIcon: {
    color: theme.palette.primary.darker,
  },

  selectedCount: {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightBold,
  },
}));

const initialSearchState = {
  appliedQuery: [""],
  limit: 25,
  titleOnly: true,
  page: 0,
  noFacets: true,
};

const AddMediaToParentMenu = React.forwardRef((props, ref) => {
  const classes = useStyles();
  const {
    active,
    open,
    closeMenu,
    childList,
    showAlert,
    parentType,
    setMenuItems,
  } = props;

  const [parentList, setParentList] = useState([]);
  const [selectedParents, setSelectedParents] = useState([]);

  const handleRowClick = useCallback(
    parent => event => {
      if (!equals(parent.status, "DELETED")) {
        switch (parentType) {
          case "anthology":
            return includes(parent.id, selectedParents)
              ? setSelectedParents([])
              : setSelectedParents([parent.id]);
          case "module": {
            const updatedModulesList = includes(parent.id, selectedParents)
              ? without([parent.id], selectedParents)
              : append(parent.id, selectedParents);
            setSelectedParents(updatedModulesList);
            return;
          }
          case "collection": {
            const updatedCollectionsList = includes(parent.id, selectedParents)
              ? without([parent.id], selectedParents)
              : append(parent.id, selectedParents);
            setSelectedParents(updatedCollectionsList);
            return;
          }
          default:
            setSelectedParents([parent.id]);
            return;
        }
      }
    },
    [selectedParents, parentType],
  );

  const [searchTerm, setSearchTerm] = useState("");
  const { state, handleUpdateQuery, handleUpdatePage } = useSearchQuery(
    mergeRight(initialSearchState, {
      appliedFacets: [
        {
          name: "type",
          queryParam: "_type",
          terms: [
            {
              code: `${toUpper(parentType)}`,
            },
          ],
        },
      ],
    }),
  );

  const debouncedFilter = useDebounce(searchTerm, 500);
  useEffect(() => {
    handleUpdateQuery([debouncedFilter]);
  }, [debouncedFilter, handleUpdateQuery]);

  const searchResults = useSearch(state);
  const [parents, setParents] = useState([]);
  useEffect(() => {
    if (searchResults.isSuccess) {
      setParents(prev =>
        state.page === 0
          ? searchResults.data.media
          : [...prev, ...searchResults.data.media],
      );
    }
  }, [searchResults.isSuccess, searchResults.data, state.page]);

  const [sentryRef, { rootRef }] = useInfiniteScroll({
    loading: searchResults.isLoading,
    hasNextPage: Boolean(searchResults?.data?.hasMore),
    onLoadMore: () => handleUpdatePage(state.page + 1),
    rootMargin: "0px 0px 400px 0px",
    delayInMs: 500,
  });

  const renderSentry = () => {
    return searchResults.data?.hasMore ? (
      <div ref={sentryRef} key="sentry">
        Loading...
      </div>
    ) : null;
  };

  //-- Monitor menu items length change
  useEffect(() => {
    setMenuItems(parentList);
  }, [parentList, setMenuItems]);

  //-- Reset parent list on close
  useEffect(() => {
    if (!open) {
      setParentList([]);
    }
  }, [open]);

  //-- Add Anthology to Module Handler
  const addToModule = useAddToModule();
  const handleModuleClick = useCallback(async () => {
    try {
      const updatedModules = selectedParents.map(async moduleId => {
        const updatedModule = await addToModule.mutateAsync({
          resourceIds: { id: moduleId },
          data: {
            ids: childList,
          },
        });
        return updatedModule;
      });

      await Promise.all(updatedModules);

      showAlert("success", ["Added Successfully"]);
      // handleAddSuccess();
    } catch (err) {
      showAlert("error", [err.message]);
    } finally {
      closeMenu();
    }
  }, [addToModule, childList, selectedParents, showAlert, closeMenu]);

  //-- Add Collection to Anthology Handler
  const addToAnthology = useAddToAnthology();
  const deleteCollectionParent = useDeleteCollectionParent();
  const handleAnthologyClick = useCallback(async () => {
    try {
      const collectionsWithParents = filter(
        collection => notBlank(collection.parentAnthologyId),
        childList,
      );

      const collections = collectionsWithParents.map(async collection => {
        const updatedCollection = await deleteCollectionParent.mutateAsync({
          resourceIds: {
            parentId: collection.parentAnthologyId,
            collectionId: collection.id,
          },
        });
        return updatedCollection;
      });

      await Promise.all(collections);

      const collectionMediaIds = childList.map(collection => collection.id);
      await addToAnthology.mutateAsync({
        resourceIds: { id: selectedParents[0] },
        data: { ids: collectionMediaIds },
      });
      showAlert("success", ["Added Successfully"]);
    } catch (err) {
      showAlert("error", [err.message]);
    } finally {
      closeMenu();
    }
  }, [
    deleteCollectionParent,
    childList,
    selectedParents,
    addToAnthology,
    showAlert,
    closeMenu,
  ]);

  //-- Add Content to Subcollection Handler
  const addToCollection = useAddToCollection();
  const handleCollectionClick = useCallback(async () => {
    //-- Call add content endpoint for each collection selected passing the media ids selected
    const results = await Promise.all(
      map(
        collectionId =>
          addToCollection.mutateAsync({
            resourceIds: { id: collectionId },
            data: { ids: childList },
          }),
        selectedParents,
      ),
    );

    const errorList = filter(notBlank, results);
    if (notBlank(errorList)) {
      showAlert("error", errorList);
    } else {
      showAlert("success", ["Content Successfully Added"]);
    }
  }, [childList, selectedParents, addToCollection, showAlert]);

  const handleNewParentClick = useCallback(() => {
    switch (parentType) {
      case "module":
        return handleModuleClick();
      case "anthology":
        return handleAnthologyClick();
      case "collection":
        return handleCollectionClick();
      default:
        return;
    }
  }, [
    parentType,
    handleModuleClick,
    handleAnthologyClick,
    handleCollectionClick,
  ]);

  if (
    anyTrue([
      addToAnthology.isPending,
      addToCollection.isPending,
      addToModule.isPending,
    ])
  ) {
    return <PanelLoadingIndicator />;
  }

  return (
    <Container
      disableGutters
      style={{ height: searchResults.isSuccess ? "100%" : "300px" }}
    >
      <Autocomplete
        open
        multiple
        disablePortal
        freeSolo
        loading={searchResults.isLoading && state.page === 0}
        clearOnBlur={false}
        options={parents}
        inputValue={searchTerm}
        renderTags={() => null}
        getOptionLabel={parent => (notBlank(parent) ? parent.name : "")}
        isOptionEqualToValue={(opt, val) =>
          notBlank(val) && equals(opt.id, val.id)
        }
        ListboxProps={{ ref: rootRef, itemSize: 80 }}
        ListboxComponent={VirtualizedListbox}
        onInputChange={(_event, value, reason) => {
          setSearchTerm(prev => (equals(reason, "reset") ? prev : value));
          handleUpdatePage(0);
        }}
        filterOptions={options => {
          if (searchResults.data?.hasMore) {
            options.push({ id: "sentry", name: "sentry", label: "" });
          }

          return options;
        }}
        classes={{
          listbox: classes.listbox,
          paper: classes.paper,
        }}
        renderOption={({ key, ...props }, parent) => {
          if (parent.id === "sentry") {
            return renderSentry();
          }

          return (
            <li key={key} {...props}>
              <Grid
                container
                alignItems="center"
                className={clsx({
                  [classes.disabled]: parent.status === "DELETED",
                })}
                onClick={handleRowClick(parent)}
              >
                <AddMediaToParentMenuItem
                  forAutocomplete
                  active={
                    active === findIndex(propEq("id", parent.id), parentList)
                  }
                  dataIndex={findIndex(propEq("id", parent.id), parentList)}
                  key={parent.id}
                  parent={parent}
                  checked={includes(parent.id, selectedParents)}
                  aria-checked={includes(parent.id, selectedParents)}
                  handleClick={handleRowClick(parent)}
                />
              </Grid>
            </li>
          );
        }}
        renderInput={params => {
          params.inputProps["aria-autocomplete"] = "none";
          params.inputProps["id"] = null;
          return (
            <div ref={params.InputProps.ref} className={classes.search}>
              <TextField
                {...params}
                autoFocus
                variant="outlined"
                placeholder="Search…"
                InputProps={{
                  classes: {
                    root: classes.inputRoot,
                    input: classes.inputInput,
                    notchedOutline: classes.outline,
                    focused: classes.inputFocused,
                  },
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        fontSize="large"
                        classes={{ root: classes.searchIcon }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setSearchTerm("")}
                        size="large"
                      >
                        <FontAwesomeIcon icon={faX} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          );
        }}
      />
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        className={classes.footer}
      >
        <Typography variant="h5" className={classes.selectedCount}>
          {selectedParents.length} selected
        </Typography>
        <Button
          id="add-media-to-parent-button"
          disabled={
            isBlank(selectedParents) ||
            anyTrue([
              addToAnthology.isPending,
              addToCollection.isPending,
              addToModule.isPending,
            ])
          }
          startIcon={<AddIcon />}
          onClick={handleNewParentClick}
        >
          Add
        </Button>
      </Grid>
    </Container>
  );
});

export default AddMediaToParentMenu;
